import { Col, Row, message } from 'antd';
import config from 'config';
import {
  GroupListSort,
  useFindAllGroupQuery,
  GroupListFilterInput,
} from 'gql/types';
import { Button, Spin, TableSearchInput } from 'modules/common/components';
import {
  useModal,
  useSort,
  useDebounce,
  useFilter,
} from 'modules/common/hooks';
import CreateGroupModal from 'modules/donor/components/CreateGroupModal';
import CreateTeamModal from 'modules/donor/components/CreateTeamModal';
import GroupList from 'modules/donor/components/GroupList';
import { useCreateTeamModalContext } from 'modules/donor/contexts/CreateTeamModal';
import ProfileSideMenuWrapper from 'modules/navigation/components/ProfileSideMenuWrapper';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

const GroupDonorListPage: FC = () => {
  const { t } = useTranslation('donor.Groups');
  const canUpdateDonors =
    useCurrentUser().data?.permissions?.updateDonorsData || false;

  const pagination = usePagination();
  const groupListSort = useSort<GroupListSort>();
  const filter = useFilter<GroupListFilterInput>();
  const groupDonorFilter = filter.getFilterObject();
  const { debounce } = useDebounce();

  const { loading, data } = useFindAllGroupQuery({
    variables: {
      input: {
        skip: pagination.skip,
        take: config.PAGINATION.DEFAULT_LIMIT,
        sort: groupListSort.options,
        filter: groupDonorFilter,
      },
    },
    onError: (error) => {
      message.error(error.message);
    },
    onCompleted: ({ findAllGroup }) => {
      pagination.setTotal(findAllGroup.meta.total);
    },
  });

  const handleSearch = (value: string) => {
    pagination.moveTo(1);
    filter.setFilterOption({
      fieldName: 'searchingQuery',
      value,
    });
  };

  const createGroupModal = useModal();
  const createTeamModalContext = useCreateTeamModalContext();
  const groups = data ? data.findAllGroup.data : [];

  const attributes = {
    groups,
    pagination,
    sorting: groupListSort,
  };

  return (
    <ProfileSideMenuWrapper title={t('pageTitle')}>
      <Spin spinning={loading}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Row gutter={[16, 10]} justify="space-between" align="middle">
              <Col flex={1}>
                <TableSearchInput
                  placeholder={t('searchGroup')}
                  maxLength={100}
                  onSearchChange={debounce(handleSearch)}
                />
              </Col>
              {canUpdateDonors && (
                <Col>
                  <Button
                    type={'primary'}
                    onClick={() => createGroupModal.show()}
                  >
                    {t('createGroup')}
                  </Button>
                </Col>
              )}
            </Row>
          </Col>
          <Col span={24}>
            <GroupList {...attributes} />
          </Col>
        </Row>
      </Spin>
      <CreateGroupModal {...createGroupModal.config} />
      {createTeamModalContext && (
        <CreateTeamModal {...createTeamModalContext.modalConfig} />
      )}
    </ProfileSideMenuWrapper>
  );
};

export default GroupDonorListPage;
